import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ApiKeyAvailableEndpointToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MultiSelect } from '~/components/multi-select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useApiKeyMutations } from '~/hooks/use-api-keys';
import { apiKeySchema, EMPTY_API_KEY, type ApiKeyFormValues } from '~/lib/schemas/api-key';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: ApiKeyFormValues | null;
	endpoints: ApiKeyAvailableEndpointToken[];
	/** Called with the one-time secret after a successful create. */
	onMinted: (token: string, keyName: string) => void;
}

/** Create / edit an API key — name, description, and scoped endpoints. Create surfaces a one-time secret. */
export function ApiKeyEditDialog({ open, onOpenChange, initial, endpoints, onMinted }: Props) {
	const { t } = useTranslation();
	const { create, update } = useApiKeyMutations();
	const form = useForm<ApiKeyFormValues>({
		resolver: zodResolver(apiKeySchema) as Resolver<ApiKeyFormValues>,
		defaultValues: initial ?? EMPTY_API_KEY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_API_KEY);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) {
				await update.mutateAsync(values);
				toast.success(t('common.save'));
			} else {
				const minted = await create.mutateAsync(values);
				if (minted.token) onMinted(minted.token, values.name.trim());
			}
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const selectedIds = watch('endpointIds');
	const selected = endpoints.filter(e => e.id && selectedIds.includes(e.id));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.apiKeys.editTitle') : t('system.apiKeys.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.apiKeys.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.apiKeys.columns.name')} error={formState.errors.name?.message}>
						<Input {...register('name')} maxLength={100} aria-invalid={Boolean(formState.errors.name)} autoFocus />
					</FormField>
					<FormField label={t('system.apiKeys.description')} error={formState.errors.description?.message}>
						<Textarea rows={2} {...register('description')} />
					</FormField>
					<FormField label={t('system.apiKeys.endpoints')}>
						<MultiSelect<ApiKeyAvailableEndpointToken>
							value={selected}
							options={endpoints}
							getKey={e => e.id ?? ''}
							getLabel={e => e.friendlyName || `${e.controllerName}.${e.actionName}`}
							onChange={next => setValue('endpointIds', next.map(e => e.id ?? '').filter(Boolean), { shouldDirty: true })}
							placeholder={t('system.apiKeys.endpointsPlaceholder')}
						/>
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : isEdit ? t('common.save') : t('system.apiKeys.generate')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
